/* eslint-disable no-undef -- Browser APIs are required for client-side downloads. */
import { isWebView } from './isWebView';
import { scheduleObjectUrlRevocation } from './scheduleObjectUrlRevocation';

const triggerDownload = (
  url: string,
  fileName?: string,
  options?: { target?: '_blank' }
) => {
  const link = document.createElement('a');

  link.href = url;
  link.rel = 'noopener noreferrer';

  if (fileName) {
    link.download = fileName;
  }

  if (options?.target) {
    link.target = options.target;
  }

  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
};

const triggerNavigationDownload = (
  url: string,
  options?: { allowNewTab?: boolean; target?: '_blank' }
) => {
  if (options?.target === '_blank' && options.allowNewTab !== false) {
    triggerDownload(url, undefined, { target: '_blank' });
    return;
  }

  window.location.assign(url);
};

const getFileNameFromDisposition = (contentDisposition: string | null) => {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8FileNameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i
  );

  if (utf8FileNameMatch?.[1]) {
    return decodeURIComponent(utf8FileNameMatch[1].trim());
  }

  const quotedFileNameMatch = contentDisposition.match(/filename="([^"]+)"/i);

  if (quotedFileNameMatch?.[1]) {
    return quotedFileNameMatch[1].trim();
  }

  const plainFileNameMatch = contentDisposition.match(/filename=([^;]+)/i);

  return plainFileNameMatch?.[1]?.trim();
};

const getFileNameFromUrl = (url: string) => {
  try {
    const { pathname } = new URL(url, window.location.href);
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    return lastSegment ? decodeURIComponent(lastSegment) : undefined;
  } catch (error) {
    return undefined;
  }
};

const getSameOrigin = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.href);
    return parsedUrl.origin === window.location.origin;
  } catch (error) {
    return null;
  }
};

export async function downloadFileByURL(url: string, fileName?: string) {
  const sameOrigin = getSameOrigin(url);
  const fallbackTarget = sameOrigin === false ? '_blank' : undefined;
  const webView = isWebView();

  if (webView) {
    triggerNavigationDownload(url, { allowNewTab: false });
    return;
  }

  try {
    const response = await fetch(
      url,
      sameOrigin ? { credentials: 'include' } : undefined
    );

    if (!response.ok) {
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const resolvedFileName =
      fileName ??
      getFileNameFromDisposition(response.headers.get('content-disposition')) ??
      getFileNameFromUrl(url);

    triggerDownload(blobUrl, resolvedFileName);
    scheduleObjectUrlRevocation(blobUrl);
  } catch (error) {
    // Direct downloads can still work even when fetch is blocked, especially
    // for cross-origin URLs like presigned S3 links without CORS headers.
    triggerDownload(url, fileName, {
      target: fallbackTarget,
    });
  }
}
